import { ChevronDown, ChevronUp, Grip } from 'lucide-react'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { useFormContext } from 'react-hook-form'

import { AssetPicker } from '@/components/AssetPicker'
import { ContentQuiz } from '@/components/ContentQuiz'
import { Button } from '@/components/elements/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/elements/collapsible'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import { PdfBlockOptions } from '@/components/PdfBlockOptions'
import { Select } from '@/components/Select'

// Articulate is available in all courses (facility + RescueX). PowerPoint is
// disabled everywhere for now (no working viewer); decks are converted to
// Articulate as needed. Re-add once its viewer works.
const CONTENT_TYPES = [
  { id: 'Quiz Only', label: 'Quiz Only' },
  { id: 'Articulate', label: 'Articulate' },
  { id: 'Video', label: 'Video' },
  { id: 'PDF', label: 'PDF' },
]

// courseId prop is accepted for call-site compatibility but ignored — the
// asset picker handles its own scope via entityId / is_xverse_asset.
const ContentBlock = ({
  content,
  index,
  name,
  orderContent,
  readOnly = false,
  remove,
  update,
}) => {
  // State
  const [contentExpanded, setContentExpanded] = useState(true)

  // Form
  const { control, getValues, watch } = useFormContext()

  // Drag & Drop
  const [{ opacity }, drop] = useDrop({
    accept: 'content',
    drop({ content: draggedContent, index: draggedIndex }) {
      if (draggedContent.id !== content.id) {
        orderContent(draggedIndex, index)
      }
    },
  })
  const [{ handlerId }, drag] = useDrag(
    () => ({
      type: 'content',
      item: { content, index },
      collect: (monitor) => ({
        opacity: monitor.isDragging() ? 0.5 : 1,
        isDragging: monitor.isDragging(),
      }),
    }),
    [content, index],
  )

  const renderAsset = () => {
    const type = watch(`${name}.type`)
    if (type === 'Quiz Only') return null

    return (
      <FormField
        control={control}
        name={`${name}.asset`}
        rules={{ required: 'An asset is required' }}
        render={() => (
          <FormItem>
            <FormLabel>Asset</FormLabel>
            <FormControl>
              <AssetPicker
                // entityId omitted → hits the global /course-assets/ endpoint,
                // which is correct for RescueX admin courses.
                type={type}
                value={watch(`${name}.asset`)}
                valueLabel={watch(`${name}.assetLabel`)}
                disabled={readOnly}
                onChange={(asset) => {
                  update(index, {
                    ...getValues(`${name}`),
                    asset: asset.id,
                    assetLabel: asset.name,
                  })
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    )
  }

  return (
    <div
      data-handler-id={handlerId}
      ref={drop}
      key={content.id}
      className="flex flex-col gap-4"
      style={{
        opacity,
      }}
    >
      <div className="flex w-full flex-row gap-4">
        <div ref={drag}>
          <Grip className="text-muted-foreground mx-auto h-6 w-6 cursor-move" />
        </div>

        <div className="flex w-full flex-col gap-4">
          <FormField
            control={control}
            disabled={readOnly}
            name={`${name}.name`}
            render={({ field: contentField }) => (
              <FormItem>
                <FormLabel>Content Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter content name" {...contentField} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
            rules={{
              // Required — students see this label in the course TOC; a blank
              // name falls back to the bare type ("Video", "PDF") which is
              // unhelpful.
              required: 'Content name is required',
            }}
          />
          <FormField
            control={control}
            disabled={readOnly}
            name={`${name}.type`}
            render={({ field: contentField }) => (
              <FormItem>
                <FormLabel>Content Type</FormLabel>
                <FormControl>
                  <Select
                    placeholder="Select Content Type"
                    options={CONTENT_TYPES}
                    className="w-full"
                    {...contentField}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
            rules={{
              required: true,
            }}
          />

          <div className="flex flex-col gap-4">
            <Collapsible open={contentExpanded} onOpenChange={setContentExpanded}>
              <CollapsibleTrigger asChild>
                <div className="flex w-full flex-row items-center justify-between pb-1">
                  <span className="text-md font-bold">Content Details</span>

                  {contentExpanded ? (
                    <ChevronUp className="text-primary size-6" />
                  ) : (
                    <ChevronDown className="text-primary size-6" />
                  )}
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent className="flex flex-col gap-4">
                {renderAsset()}

                {watch(`${name}.type`) === 'PDF' && (
                  <PdfBlockOptions name={name} readOnly={readOnly} />
                )}

                {content.quiz && <ContentQuiz name={name} readOnly={readOnly} />}

                <div className="flex w-full flex-row justify-between">
                  <div className="flex w-full flex-row gap-2">
                    {!readOnly && (
                      <Button
                        type="button"
                        variant={content.quiz ? 'destructive' : 'outline'}
                        onClick={() => {
                          if (content.quiz) {
                            update(index, {
                              ...getValues(`${name}`),
                              quiz: null,
                            })
                          } else {
                            update(index, {
                              ...getValues(`${name}`),
                              quiz: {
                                questions: [],
                                name: '',
                                numQuestionsToPass: 0,
                                numQuestionsToDisplay: 0,
                              },
                            })
                          }
                        }}
                      >
                        {!content.quiz ? 'Add' : 'Remove'} Quiz
                      </Button>
                    )}
                  </div>

                  {!readOnly && (
                    <Button type="button" variant="destructive" onClick={() => remove(index)}>
                      Remove Content Block
                    </Button>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div>
      </div>
    </div>
  )
}

ContentBlock.propTypes = {
  // Kept for call-site compatibility but no longer used (assets have their
  // own S3 prefix, scoped by is_xverse_asset and/or entity).
  courseId: PropTypes.string,
  content: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  orderContent: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  remove: PropTypes.func.isRequired,
  update: PropTypes.func.isRequired,
}

export default ContentBlock
